import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, GripVertical } from 'lucide-react'
import evaluationGridService, { CreateEvaluationGridDto, EvaluationCriteria } from '../../../services/evaluationGridService'
import classService from '../../../services/classService'

type Step = 'info' | 'criteria' | 'review'

export function CreateEvaluationGridPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<Step>('info')
  const [formData, setFormData] = useState<CreateEvaluationGridDto>({
    name: '',
    classId: '',
    academicYear: '2024-2025',
    semester: 'S1',
    criteria: []
  })

  const { data: classes } = useQuery({
    queryKey: ['classes'],
    queryFn: () => classService.getAll()
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateEvaluationGridDto) => evaluationGridService.create(data),
    onSuccess: () => {
      navigate('/modules/evaluation/grids')
    }
  })

  const handleNext = () => {
    if (currentStep === 'info' && validateInfoStep()) {
      setCurrentStep('criteria')
    } else if (currentStep === 'criteria' && validateCriteriaStep()) {
      setCurrentStep('review')
    }
  }

  const handleBack = () => {
    if (currentStep === 'criteria') setCurrentStep('info')
    else if (currentStep === 'review') setCurrentStep('criteria')
  }

  const validateInfoStep = () => {
    return formData.name && formData.classId && formData.academicYear && formData.semester
  }

  const validateCriteriaStep = () => {
    return formData.criteria.length > 0 && formData.criteria.every(c => c.name && c.coefficient > 0)
  }

  const handleSubmit = async () => {
    if (validateInfoStep() && validateCriteriaStep()) {
      await createMutation.mutateAsync(formData)
    }
  }

  const addCriteria = () => {
    setFormData({
      ...formData,
      criteria: [...formData.criteria, { name: '', description: '', coefficient: 1 }]
    })
  }

  const updateCriteria = (index: number, updates: Partial<EvaluationCriteria>) => {
    const newCriteria = [...formData.criteria]
    newCriteria[index] = { ...newCriteria[index], ...updates }
    setFormData({ ...formData, criteria: newCriteria })
  }

  const removeCriteria = (index: number) => {
    setFormData({
      ...formData,
      criteria: formData.criteria.filter((_, i) => i !== index)
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/modules/evaluation/grids')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour aux grilles</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[
              { key: 'info', label: 'Informations' },
              { key: 'criteria', label: 'Critères' },
              { key: 'review', label: 'Résumé' }
            ].map((step, index) => (
              <div key={step.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep === step.key
                        ? 'bg-violet-600 text-white'
                        : index < ['info', 'criteria', 'review'].indexOf(currentStep)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {index < ['info', 'criteria', 'review'].indexOf(currentStep) ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className="text-sm mt-2 font-medium text-gray-700">{step.label}</span>
                </div>
                {index < 2 && (
                  <div
                    className={`h-1 flex-1 mx-2 ${
                      index < ['info', 'criteria', 'review'].indexOf(currentStep)
                        ? 'bg-green-500'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 p-8">
          {currentStep === 'info' && (
            <InfoStep formData={formData} setFormData={setFormData} classes={classes || []} />
          )}
          {currentStep === 'criteria' && (
            <CriteriaStep
              criteria={formData.criteria}
              addCriteria={addCriteria}
              updateCriteria={updateCriteria}
              removeCriteria={removeCriteria}
            />
          )}
          {currentStep === 'review' && <ReviewStep formData={formData} classes={classes || []} />}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4 mt-6">
          {currentStep !== 'info' && (
            <button
              onClick={handleBack}
              className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Précédent
            </button>
          )}
          <div className="flex-1" />
          {currentStep !== 'review' ? (
            <button
              onClick={handleNext}
              disabled={currentStep === 'info' ? !validateInfoStep() : !validateCriteriaStep()}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
            >
              <span>Suivant</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
            >
              {createMutation.isPending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              <span>Créer la grille</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function InfoStep({
  formData,
  setFormData,
  classes
}: {
  formData: CreateEvaluationGridDto
  setFormData: (data: CreateEvaluationGridDto) => void
  classes: any[]
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Informations de la grille</h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la grille *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          placeholder="Ex: Évaluation comportementale S1"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Classe *</label>
        <select
          value={formData.classId}
          onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
        >
          <option value="">Sélectionnez une classe</option>
          {classes.map((cls) => (
            <option key={cls.id} value={cls.id}>
              {cls.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Année scolaire *</label>
          <input
            type="text"
            value={formData.academicYear}
            onChange={(e) => setFormData({ ...formData, academicYear: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            placeholder="2024-2025"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Semestre *</label>
          <select
            value={formData.semester}
            onChange={(e) => setFormData({ ...formData, semester: e.target.value as 'S1' | 'S2' })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          >
            <option value="S1">Semestre 1</option>
            <option value="S2">Semestre 2</option>
          </select>
        </div>
      </div>
    </div>
  )
}

function CriteriaStep({
  criteria,
  addCriteria,
  updateCriteria,
  removeCriteria
}: {
  criteria: Omit<EvaluationCriteria, 'id' | 'evaluationGridId'>[]
  addCriteria: () => void
  updateCriteria: (index: number, updates: Partial<EvaluationCriteria>) => void
  removeCriteria: (index: number) => void
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Critères d'évaluation</h2>
        <button
          onClick={addCriteria}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Ajouter</span>
        </button>
      </div>

      {criteria.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-600 mb-4">Aucun critère défini</p>
          <button
            onClick={addCriteria}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter le premier critère</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {criteria.map((criterion, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="cursor-move pt-2">
                  <GripVertical className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1 space-y-3">
                  <input
                    type="text"
                    value={criterion.name}
                    onChange={(e) => updateCriteria(index, { name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Nom du critère"
                  />
                  <textarea
                    value={criterion.description || ''}
                    onChange={(e) => updateCriteria(index, { description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Description (optionnel)"
                    rows={2}
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Coefficient:</label>
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={criterion.coefficient}
                      onChange={(e) => updateCriteria(index, { coefficient: parseFloat(e.target.value) })}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeCriteria(index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ReviewStep({
  formData,
  classes
}: {
  formData: CreateEvaluationGridDto
  classes: any[]
}) {
  const selectedClass = classes.find((c) => c.id === formData.classId)

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Récapitulatif</h2>

      <div className="space-y-4">
        <div className="bg-violet-50 rounded-lg p-4">
          <h3 className="font-semibold text-violet-900 mb-3">Informations</h3>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-violet-700">Nom:</dt>
              <dd className="font-medium text-violet-900">{formData.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-violet-700">Classe:</dt>
              <dd className="font-medium text-violet-900">{selectedClass?.name || 'N/A'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-violet-700">Année:</dt>
              <dd className="font-medium text-violet-900">{formData.academicYear}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-violet-700">Semestre:</dt>
              <dd className="font-medium text-violet-900">{formData.semester}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900 mb-3">
            Critères ({formData.criteria.length})
          </h3>
          <div className="space-y-2">
            {formData.criteria.map((criterion, index) => (
              <div key={index} className="bg-white rounded-lg p-3 border border-purple-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-purple-900">{criterion.name}</p>
                    {criterion.description && (
                      <p className="text-sm text-purple-700 mt-1">{criterion.description}</p>
                    )}
                  </div>
                  <span className="ml-3 px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium">
                    Coef. {criterion.coefficient}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
